import { useEffect, useState, useMemo } from "react";
import { Search, Plus, Pencil, Trash2, Music } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import SongForm from "@/components/SongForm";
import {
  fetchSongs,
  createSong,
  updateSong,
  deleteSong,
} from "@/lib/api";
import { formatDuration } from "@/lib/format";

export default function Songs() {
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = () =>
    fetchSongs()
      .then((data) => setSongs(data))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return songs;
    return songs.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.artist.toLowerCase().includes(q)
    );
  }, [songs, query]);

  const handleSubmit = async (data) => {
    try {
      if (editing) {
        await updateSong(editing.id, data);
        toast.success("Música atualizada");
      } else {
        await createSong(data);
        toast.success("Música cadastrada");
      }
      setDialogOpen(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Erro ao salvar música");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSong(deleteId);
      toast.success("Música excluída");
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error("Erro ao excluir");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-[#00F0FF] mb-2">
            // banco de músicas
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Músicas cadastradas
          </h1>
          <p className="text-zinc-500 mt-2 text-sm md:text-base">
            Centralize tom, BPM, duração e configuração de equipamento de cada faixa.
          </p>
        </div>

        <Dialog
          open={dialogOpen}
          onOpenChange={(o) => {
            setDialogOpen(o);
            if (!o) setEditing(null);
          }}
        >
          <DialogTrigger asChild>
            <Button
              className="bg-[#00F0FF] hover:bg-[#33F3FF] text-black font-semibold h-11"
              data-testid="add-song-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Nova música
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#1A1A1A] border-[#2D2D2D] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold tracking-tight">
                {editing ? "Editar música" : "Nova música"}
              </DialogTitle>
            </DialogHeader>
            <SongForm
              initial={editing}
              onSubmit={handleSubmit}
              onCancel={() => {
                setDialogOpen(false);
                setEditing(null);
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mb-6 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por título ou artista..."
          data-testid="search-songs-input"
          className="pl-9 bg-[#1A1A1A] border-[#2D2D2D] text-white placeholder:text-zinc-600 h-11 focus-visible:ring-[#00F0FF]"
        />
      </div>

      <div className="rounded-lg border border-[#2D2D2D] bg-[#1A1A1A] overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-zinc-500">Carregando...</p>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Music className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
            <p className="text-zinc-400">
              {query
                ? "Nenhuma música encontrada para essa busca."
                : "Nenhuma música cadastrada ainda."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="songs-table">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#2D2D2D] text-left">
                  <th className="py-3 px-4 text-xs font-mono uppercase tracking-wider text-zinc-500">
                    Título
                  </th>
                  <th className="py-3 px-4 text-xs font-mono uppercase tracking-wider text-zinc-500 hidden md:table-cell">
                    Artista
                  </th>
                  <th className="py-3 px-4 text-xs font-mono uppercase tracking-wider text-zinc-500">
                    Tom
                  </th>
                  <th className="py-3 px-4 text-xs font-mono uppercase tracking-wider text-zinc-500 hidden sm:table-cell">
                    BPM
                  </th>
                  <th className="py-3 px-4 text-xs font-mono uppercase tracking-wider text-zinc-500">
                    Duração
                  </th>
                  <th className="py-3 px-4 text-xs font-mono uppercase tracking-wider text-zinc-500 text-right">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((song) => (
                  <tr
                    key={song.id}
                    data-testid={`song-row-${song.id}`}
                    className="border-b border-[#2D2D2D] last:border-0 hover:bg-[#222]/40 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="font-medium">{song.title}</div>
                      <div className="text-xs text-zinc-500 md:hidden">
                        {song.artist}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-zinc-400 hidden md:table-cell">
                      {song.artist}
                    </td>
                    <td className="py-3 px-4 font-mono text-sm text-[#00F0FF]">
                      {song.key || "—"}
                    </td>
                    <td className="py-3 px-4 font-mono text-sm text-zinc-300 hidden sm:table-cell">
                      {song.bpm || "—"}
                    </td>
                    <td className="py-3 px-4 font-mono text-sm text-zinc-300">
                      {formatDuration(song.duration_seconds)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-song-${song.id}`}
                          className="text-zinc-400 hover:text-white hover:bg-[#2D2D2D] h-8 w-8 p-0"
                          onClick={() => {
                            setEditing(song);
                            setDialogOpen(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-song-${song.id}`}
                          className="text-zinc-400 hover:text-red-400 hover:bg-[#2D2D2D] h-8 w-8 p-0"
                          onClick={() => setDeleteId(song.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1A1A1A] border-[#2D2D2D] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir música?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Esta ação não pode ser desfeita. A música será removida do banco e de todos os setlists.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#2D2D2D] text-white hover:bg-[#2D2D2D]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-song"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
